import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ClientInferRequest } from "@ts-rest/core";
import { useSearchParams } from "react-router-dom";

import { client } from "~/api/client";
import { autoId } from "~/utils/auto-id";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import { getAuth, isProfileAuth } from "~/providers/store/auth";
import { logger } from "~/services/logger";
import { basketActions } from "~/providers/store/basket";
import { type apiContract } from "~/api/contract";
import { ordersKeys } from "~/hooks/queries/orders";
import { useInvalidateProducts } from "~/api/products/hooks/use-invalidate-products";
import { DisplayError, DisplayErrorCodeEnum } from "~/utils/display-error";
import {
  ApiErrorCodesEnum,
  isApiErrorCode,
  isTransientRequestError
} from "~/api/error";
import { recoverCardOrder } from "~/utils/recover-card-order";

export type CreateCardRequest = ClientInferRequest<
  typeof apiContract.v3.pos.orders.create
>;

type UseOrderCardCreateProps = {
  /**
   * If the timeout is reached, the transaction will be aborted.
   * @param args args needed to the abort mutation and cancel a transaction
   */
  abortTransactionCallback: (args: {
    orderId: string;
    serviceId: string;
    terminalId: string;
  }) => void;
};

export function useOrderCardCreateV3({
  abortTransactionCallback
}: UseOrderCardCreateProps) {
  const queryClient = useQueryClient();
  const [, setSearchParams] = useSearchParams();
  const invalidateProducts = useInvalidateProducts();

  const { data: till } = useCurrentTill();

  return useMutation({
    mutationFn: async ({
      employeeUserId,
      employeeChildId,
      paidWithSaldoUnit,
      sessionId,
      ...body
    }: Omit<
      CreateCardRequest["body"],
      | "tillId"
      | "moduleId"
      | "schoolId"
      | "userId"
      | "childId"
      | "profileId"
      | "serviceId"
      | "partnerId"
    > & {
      employeeUserId: string | null;
      employeeChildId: string | null;
      employeeCardHex: string | null;
      paidWithSaldoUnit: number | null;
    }) => {
      basketActions.setCardPaymentInProgress(true);

      const auth = getAuth();

      if (!isProfileAuth(auth)) {
        throw new DisplayError(DisplayErrorCodeEnum.PROFILE_NOT_SELECTED);
      }

      if (!till?.terminalId) {
        throw new Error("Terminal is not connected");
      }

      const terminalId = till.terminalId;
      const serviceId = autoId(10);
      const startedAt = Date.now();

      setSearchParams((searchParams) => {
        searchParams.set("serviceId", serviceId);
        searchParams.set("orderId", body.orderId);
        return searchParams;
      });

      // The order id is minted on the till, so when the response is lost the
      // order itself tells us whether the terminal was charged. Retrying the
      // request instead would charge again under a fresh service id.
      const recoverAfterLostResponse = async (reason: string) => {
        logger.warn("Card order response lost, recovering from the order", {
          order_id: body.orderId,
          service_id: serviceId,
          reason
        });
        basketActions.setCardPaymentRecovering(true);

        const result = await recoverCardOrder({
          startedAt,
          fetchOrder: () =>
            client.v1.pos.orders.getById({ params: { orderId: body.orderId } }),
          isTerminalTransactionKnown: async () => {
            const transaction = await client.v1.pos.terminals.status({
              params: { terminalId },
              query: { serviceId, tillId: auth.tillId }
            });

            return transaction.status === 200;
          }
        });

        logger.info("Card order recovered", {
          order_id: body.orderId,
          service_id: serviceId,
          reason,
          outcome: result.outcome,
          event: "card_order_recovered"
        });

        switch (result.outcome) {
          case "paid":
            return { orderId: body.orderId, id: result.paymentId };
          case "cancelled":
            throw new DisplayError(DisplayErrorCodeEnum.POI_USER_CANCEL);
          case "not_found":
            throw new DisplayError(DisplayErrorCodeEnum.CARD_ORDER_NOT_SENT);
          case "timed_out":
            abortTransactionCallback({
              orderId: body.orderId,
              serviceId,
              terminalId
            });
            throw new DisplayError(DisplayErrorCodeEnum.CARD_ORDER_TIMED_OUT);
        }
      };

      let res: Awaited<ReturnType<typeof client.v3.pos.orders.create>>;

      try {
        res = await client.v3.pos.orders.create({
          body: {
            ...body,
            tillId: auth.tillId,
            moduleId: auth.moduleId,
            schoolId: auth.schoolId,
            profileId: auth.profileId,
            childId: employeeChildId ?? auth.childId,
            userId: employeeUserId ?? auth.userId,
            paidWithSaldoUnit,
            serviceId,
            partnerId: auth.partnerId,
            sessionId
          }
        });
      } catch (error) {
        if (!isTransientRequestError(error)) {
          throw error;
        }

        return recoverAfterLostResponse("network_error");
      }

      if (res.status === 201) {
        return res.body;
      }

      if (isApiErrorCode(res, ApiErrorCodesEnum.ORDER_ALREADY_PAID)) {
        return recoverAfterLostResponse(ApiErrorCodesEnum.ORDER_ALREADY_PAID);
      }

      if (isApiErrorCode(res, ApiErrorCodesEnum.ORDER_PAYMENT_IN_PROGRESS)) {
        return recoverAfterLostResponse(
          ApiErrorCodesEnum.ORDER_PAYMENT_IN_PROGRESS
        );
      }

      // Check Adyen transaction status to handle cases where something went wrong on the order endpoint
      const transactionStatus = await client.v1.pos.terminals.status({
        params: {
          terminalId
        },
        query: {
          serviceId,
          tillId: auth.tillId
        }
      });

      if (transactionStatus.status === 200) {
        if (transactionStatus.body.status === "success") {
          if (transactionStatus.body.transactionStatus === "in_progress") {
            abortTransactionCallback({
              orderId: body.orderId,
              serviceId,
              terminalId
            });

            throw new Error("Transaction timed out");
          }

          if (transactionStatus.body.transactionStatus === "approved") {
            const order = await client.v1.pos.orders.getById({
              params: {
                orderId: body.orderId
              }
            });

            if (order.status === 200) {
              if (order.body.status === "paid") {
                return {
                  orderId: body.orderId,
                  id: order.body.payments?.[0].id ?? "unknown"
                };
              }
              // TO-DO: refund or mark order as paid (if we see this happening)
              const message = `Order was paid, but the order is not marked paid: ${body.orderId}`;
              logger.error(new Error(message), message, {
                order_id: body.orderId
              });
            } else {
              // TO-DO: refund (if we see this happening)
              const message = `Order was paid, but the order could not be fetched: ${body.orderId}`;
              logger.error(new Error(message), message, {
                order_id: body.orderId
              });
            }
          }
        }
      }

      throw res;
    },
    onSuccess: async (res) => {
      await queryClient.invalidateQueries({
        queryKey: ordersKeys.detail(res.orderId).queryKey
      });
    },
    onError: async (_, { orderId }) => {
      invalidateProducts(null);
      await queryClient.invalidateQueries({
        queryKey: ordersKeys.detail(orderId).queryKey
      });
    },
    onSettled: () => {
      basketActions.setCardPaymentInProgress(false);
      basketActions.setCardPaymentRecovering(false);
    }
  });
}
